import { useEffect, useState } from 'react'

import { Button } from 'ui'
import { Collapsable, DeleteButton } from 'components/UI'
import { FileUpload } from 'components/Forms'
import { useUpload, useGetUrl } from 'components/Hooks'
import { UPLOAD_ATTACHMENTS_TYPES } from 'common'

import { t } from 'translations'

interface ProgressBarProps {
  progress: number
}

const ProgressBar: React.FC<ProgressBarProps> = ({ progress }) => {
  return (
    <div className="my-2 progress w-full">
      <div
        className="progress-bar bg-success"
        style={{ width: `${progress}%` }}
        role="progressbar"
        aria-valuenow={progress}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  )
}

interface Attachment {
  key?: string
  name: string
  type?: string
  url?: string
}
interface AttachmentProps {
  files: Attachment[]
  _id: string
  setFieldValue: (field: string, value: Attachment[], shouldValidate?: boolean | undefined) => void
}

const getFileType = (fileName: string) => {
  const [fileType] = fileName.split('.').reverse()
  return fileType
}

const Attachments: React.FC<AttachmentProps> = ({ files, _id, setFieldValue }) => {
  const { uploadHandler, progress } = useUpload({ attachmentType: UPLOAD_ATTACHMENTS_TYPES.SHIFT })
  const { getUrlHandler } = useGetUrl({ attachmentType: UPLOAD_ATTACHMENTS_TYPES.SHIFT })
  const [filesList, setFilesList] = useState<Attachment[]>([])
  const [isFetched, setIsFetched] = useState(false)

  useEffect(() => {
    if (files.length === 0 || isFetched) return
    const getUrls = async () => {
      for (const file of files) {
        if (!file.key) continue
        const url = await getUrlHandler({ key: file.key })
        const fileType = getFileType(file.name)
        if (url) setFilesList(prev => [...prev, { name: file.name, url: url, key: file.key, type: fileType }])
      }
    }
    getUrls()
    setIsFetched(true)
  }, [files, getUrlHandler, isFetched])

  useEffect(() => {
    const updatedFiles = filesList.map(({ name, key, type }) => ({ name, key, type }))
    setFieldValue('files', updatedFiles)
  }, [filesList, setFieldValue])

  const handleFileDrop = async (file?: File) => {
    if (!file) return
    const key = await uploadHandler({ fileName: file.name, file, _id, contentType: file.type })
    if (!key) return
    const url = await getUrlHandler({ key })
    if (!url) return
    const fileType = getFileType(file.name)
    setFilesList(prev => [...prev, { name: file.name, url, key, type: fileType }])
  }

  const handleDelete = async file => {
    setFilesList(prev => [...prev].filter(selected => selected.key !== file.key))
  }

  return (
    <Collapsable type="attachment" title={t('forms.fileUpload.ATTACHMENTS')} show={files.length > 0}>
      <FileUpload onChange={handleFileDrop} />
      <div>
        {progress && progress.length > 0
          ? progress.map(file => (
              <div key={file.name}>
                <div>{file.name}</div>
                <ProgressBar progress={file.progress} />
              </div>
            ))
          : null}
      </div>
      {filesList.length > 0 ? (
        <div className="border rounded">
          {filesList.map((file, index) => (
            <div key={index}>
              <div className="d-flex flex-row justify-content-between">
                <a href={file.url} target="_blank" rel="noreferrer">
                  <Button type="button" isLink>
                    <div className="m-2 d-flex flex-row align-items-center">
                      <div>
                        <div className="fa-regular fa-file" />
                      </div>
                      <div className="ml-2 text-break">{file.name}</div>
                    </div>
                  </Button>
                </a>
                <div>
                  <div className="text-center m-2">
                    <DeleteButton onClick={() => handleDelete(file)} title={'manager.schedule.ATTACHMENTS.DELETE_BUTTON'} disableClose />
                  </div>
                </div>
              </div>
              {index !== filesList.length - 1 ? <div className="border-bottom"></div> : null}
            </div>
          ))}
        </div>
      ) : null}
    </Collapsable>
  )
}

export default Attachments
